/**
 * ProjectConversationsList Component
 *
 * Pure presentation component that displays a list of previous conversations for a project.
 * Clicking a conversation should navigate to that conversation.
 *
 * @package @ideoz/ui
 */

import React from 'react';
import { MessageSquare } from 'lucide-react';
import { Button } from '../atoms/button';
import { Skeleton } from '../atoms/skeleton';
import Icon from '../molecules/Icon';
import { ConversationMode } from '@ideoz/core';
import { formatDate } from '@ideoz/ui/utils';

export interface ProjectConversation {
  id: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  mode?: ConversationMode;
}

export interface ProjectConversationsListProps {
  conversations: ProjectConversation[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onConversationClick: (
    conversationId: string,
    conversationTitle?: string,
    mode?: ConversationMode
  ) => void;
}

const ConversationSkeleton = () => (
  <div className="flex items-center gap-3 p-3 bg-gray-25 rounded-lg">
    <div className="p-2 bg-gray-100">
      <Skeleton className="h-3 w-3" />
    </div>
    <Skeleton className="h-4 flex-1" />
  </div>
);

export const ProjectConversationsList: React.FC<ProjectConversationsListProps> = ({
  conversations,
  isLoading,
  hasMore,
  onLoadMore,
  onConversationClick,
}) => {
  // Show skeleton loading
  if (isLoading && conversations.length === 0) {
  return (
  <div className="mt-8" data-testid="project-conversations-skeleton">
    {/* Title Skeleton */}
    <Skeleton className="h-4 w-32 mb-4" />

    {/* List Container */}
    <div className="glass-subtle rounded-xl border border-glass-border/100 overflow-hidden divide-y divide-glass-border/30">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between px-4 py-3.5"
        >
          {/* Left side (icon + title) */}
          <div className="flex items-center gap-3">
            <Skeleton className="w-4 h-4 rounded-sm" />
            <Skeleton className="h-3.5 w-40" />
          </div>

          {/* Date */}
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  </div>
);

  }

  // Don't render if no conversations
  if (!conversations || conversations.length === 0) {
    return null;
  }

  console.log('conversations', conversations);

  return (
    <div className="mt-8" data-testid="project-conversations-section">
      <h3 className="text-gray-800 text-start text-base font-medium leading-[120%] mb-4">
        Previous chats
      </h3>
      <div
        className="glass-subtle rounded-xl border border-glass-border/100 overflow-hidden divide-y divide-glass-border/30"
        data-testid="project-conversations-list"
      >
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className="flex items-center justify-between px-4 py-3.5 hover:bg-glass/80 transition-all duration-200 cursor-pointer group"
            onClick={() =>
              onConversationClick(
                conversation.id,
                conversation.title,
                conversation['mode'] as ConversationMode
              )
            }
            data-testid={`conversation-item-${conversation.id}`}
          >
            <div className="flex items-center gap-3">
              <Icon
                icon={MessageSquare}
                size={14}
                className=" w-4 h-4 stroke-muted-foreground group-hover:stroke-glass-foreground transition-colors"
              />
              <span className="text-sm text-glass-foreground group-hover:text-blue-light-800 transition-colors">
                {conversation.title || 'Untitled conversation'}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {formatDate(conversation.updated_at)}
            </span>
          </div>
        ))}

        {/* Load More button */}
        {hasMore && (
          <div className="flex justify-center mt-4">
            <Button
              variant="link-gray"
              onClick={onLoadMore}
              disabled={isLoading}
              className="px-6 py-2"
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectConversationsList;
